/**
 * 벌점 관리 서비스
 */
import { API_BASE_URL } from '../../../config/api';
import {
  PenaltyType,
  PenaltyTypeCreate,
  PenaltyTypeUpdate,
  PenaltySetting,
  PenaltySettingUpdate,
  PenaltyThreshold,
  PenaltyThresholdCreate,
  PenaltyThresholdUpdate,
  PenaltyRecord,
  PenaltyRecordCreate,
  PenaltyRecordFilter,
  PenaltySummary,
  PenaltySummaryFilter,
  PenaltyStatistics,
} from '../models/Penalty';

// API base URL
const getApiUrl = (path: string) => {
  const baseUrl = API_BASE_URL === '' ? '/api' : API_BASE_URL;
  return `${baseUrl}${path}`;
};

// 인증 헤더 생성
const getAuthHeaders = () => {
  // auth_access_token 우선, accessToken은 호환성 유지 (다른 서비스와 동일한 패턴)
  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// API 응답 처리
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: '요청 처리 중 오류가 발생했습니다.' }));
    throw new Error(error.detail || error.message || `HTTP Error ${response.status}`);
  }
  return response.json();
};

/**
 * 벌점 유형 API
 */
export const penaltyTypeService = {
  // 벌점 유형 목록 조회
  getTypes: async (
    organizationId: string,
    includeInactive: boolean = false
  ): Promise<PenaltyType[]> => {
    const params = new URLSearchParams({
      organization_id: organizationId,
      include_inactive: String(includeInactive),
    });
    const response = await fetch(getApiUrl(`/penalties/types?${params}`), {
      headers: getAuthHeaders(),
    });
    return handleResponse<PenaltyType[]>(response);
  },

  // 벌점 유형 생성
  createType: async (data: PenaltyTypeCreate): Promise<PenaltyType> => {
    const response = await fetch(getApiUrl('/penalties/types'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PenaltyType>(response);
  },

  // 벌점 유형 수정
  updateType: async (typeId: string, data: PenaltyTypeUpdate): Promise<PenaltyType> => {
    const response = await fetch(getApiUrl(`/penalties/types/${typeId}`), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PenaltyType>(response);
  },

  // 벌점 유형 삭제
  deleteType: async (typeId: string): Promise<void> => {
    const response = await fetch(getApiUrl(`/penalties/types/${typeId}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '삭제 중 오류가 발생했습니다.' }));
      throw new Error(error.detail || error.message);
    }
  },

  // 기본 유형 초기화
  initTypes: async (organizationId: string): Promise<{ created_count: number; message: string }> => {
    const params = new URLSearchParams({ organization_id: organizationId });
    const response = await fetch(getApiUrl(`/penalties/types/init?${params}`), {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<{ created_count: number; message: string }>(response);
  },
};

/**
 * 벌점 임계값 API
 */
export const penaltyThresholdService = {
  // 임계값 목록 조회
  getThresholds: async (
    organizationId: string,
    includeInactive: boolean = false
  ): Promise<PenaltyThreshold[]> => {
    const params = new URLSearchParams({
      organization_id: organizationId,
      include_inactive: String(includeInactive),
    });
    const response = await fetch(getApiUrl(`/penalties/thresholds?${params}`), {
      headers: getAuthHeaders(),
    });
    return handleResponse<PenaltyThreshold[]>(response);
  },

  // 임계값 생성
  createThreshold: async (data: PenaltyThresholdCreate): Promise<PenaltyThreshold> => {
    const response = await fetch(getApiUrl('/penalties/thresholds'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PenaltyThreshold>(response);
  },

  // 임계값 수정
  updateThreshold: async (
    thresholdId: string,
    data: PenaltyThresholdUpdate
  ): Promise<PenaltyThreshold> => {
    const response = await fetch(getApiUrl(`/penalties/thresholds/${thresholdId}`), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PenaltyThreshold>(response);
  },

  // 임계값 삭제
  deleteThreshold: async (thresholdId: string): Promise<void> => {
    const response = await fetch(getApiUrl(`/penalties/thresholds/${thresholdId}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '삭제 중 오류가 발생했습니다.' }));
      throw new Error(error.detail || error.message);
    }
  },
};

/**
 * 벌점 설정 API
 */
export const penaltySettingService = {
  // 설정 조회
  getSetting: async (organizationId: string): Promise<PenaltySetting> => {
    const params = new URLSearchParams({ organization_id: organizationId });
    const response = await fetch(getApiUrl(`/penalties/settings?${params}`), {
      headers: getAuthHeaders(),
    });
    return handleResponse<PenaltySetting>(response);
  },

  // 설정 수정
  updateSetting: async (
    organizationId: string,
    data: PenaltySettingUpdate
  ): Promise<PenaltySetting> => {
    const params = new URLSearchParams({ organization_id: organizationId });
    const response = await fetch(getApiUrl(`/penalties/settings?${params}`), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PenaltySetting>(response);
  },
};

// 페이지네이션 응답 타입
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * 벌점 기록 API
 */
export const penaltyRecordService = {
  // 기록 목록 조회 (서버 사이드 페이지네이션)
  getRecords: async (
    organizationId: string,
    filter?: PenaltyRecordFilter,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedResponse<PenaltyRecord>> => {
    const params = new URLSearchParams({
      organization_id: organizationId,
      page: String(page),
      page_size: String(pageSize),
    });
    if (filter) {
      if (filter.student_id) params.append('student_id', filter.student_id);
      if (filter.penalty_type) params.append('penalty_type', filter.penalty_type);
      if (filter.start_date) params.append('start_date', filter.start_date);
      if (filter.end_date) params.append('end_date', filter.end_date);
      if (filter.include_cancelled !== undefined) {
        params.append('include_cancelled', String(filter.include_cancelled));
      }
    }
    const response = await fetch(getApiUrl(`/penalties/records?${params}`), {
      headers: getAuthHeaders(),
    });
    return handleResponse<PaginatedResponse<PenaltyRecord>>(response);
  },

  // 기록 상세 조회
  getRecord: async (recordId: string): Promise<PenaltyRecord> => {
    const response = await fetch(getApiUrl(`/penalties/records/${recordId}`), {
      headers: getAuthHeaders(),
    });
    return handleResponse<PenaltyRecord>(response);
  },

  // 벌점 부과
  createRecord: async (data: PenaltyRecordCreate): Promise<PenaltyRecord> => {
    const response = await fetch(getApiUrl('/penalties/records'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PenaltyRecord>(response);
  },

  // 벌점 취소
  cancelRecord: async (recordId: string, cancelReason: string): Promise<void> => {
    const params = new URLSearchParams({ cancel_reason: cancelReason });
    const response = await fetch(getApiUrl(`/penalties/records/${recordId}?${params}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '취소 중 오류가 발생했습니다.' }));
      throw new Error(error.detail || error.message);
    }
  },

  // 벌점 감경
  reduceRecord: async (recordId: string, reducedPoints: number, reason: string): Promise<any> => {
    const response = await fetch(getApiUrl(`/penalties/records/${recordId}/reduce`), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reduced_points: reducedPoints, reason }),
    });
    return handleResponse(response);
  },

  // 벌점 복구
  restoreRecord: async (recordId: string): Promise<void> => {
    const response = await fetch(getApiUrl(`/penalties/records/${recordId}/restore`), {
      method: 'PUT',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '복구 중 오류가 발생했습니다.' }));
      throw new Error(error.detail || error.message);
    }
  },

  // 감경 취소 (원복)
  cancelReduction: async (recordId: string): Promise<any> => {
    const response = await fetch(getApiUrl(`/penalties/records/${recordId}/reduce`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
};

/**
 * 벌점 현황 API
 */
export const penaltySummaryService = {
  // 현황 목록 조회 (서버 사이드 페이지네이션)
  getSummaries: async (
    organizationId: string,
    filter?: PenaltySummaryFilter,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedResponse<PenaltySummary>> => {
    const params = new URLSearchParams({
      organization_id: organizationId,
      page: String(page),
      page_size: String(pageSize),
    });
    if (filter) {
      if (filter.min_points !== undefined) params.append('min_points', String(filter.min_points));
    }
    const response = await fetch(getApiUrl(`/penalties/summaries?${params}`), {
      headers: getAuthHeaders(),
    });
    return handleResponse<PaginatedResponse<PenaltySummary>>(response);
  },

  // 특정 학생 현황 조회
  getStudentSummary: async (
    studentId: string,
    organizationId: string
  ): Promise<PenaltySummary> => {
    const params = new URLSearchParams({ organization_id: organizationId });
    const response = await fetch(getApiUrl(`/penalties/summaries/${studentId}?${params}`), {
      headers: getAuthHeaders(),
    });
    return handleResponse<PenaltySummary>(response);
  },
};

/**
 * 벌점 통계/초기화 API
 */
export const penaltyStatisticsService = {
  // 통계 조회
  getStatistics: async (organizationId: string): Promise<PenaltyStatistics> => {
    const params = new URLSearchParams({ organization_id: organizationId });
    const response = await fetch(getApiUrl(`/penalties/statistics?${params}`), {
      headers: getAuthHeaders(),
    });
    return handleResponse<PenaltyStatistics>(response);
  },

  // 벌점 초기화
  resetPenalties: async (organizationId: string): Promise<{ reset_count: number }> => {
    const params = new URLSearchParams({ organization_id: organizationId });
    const response = await fetch(getApiUrl(`/penalties/reset?${params}`), {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<{ reset_count: number }>(response);
  },
};

// 통합 서비스 export
export const penaltyService = {
  types: penaltyTypeService,
  thresholds: penaltyThresholdService,
  settings: penaltySettingService,
  records: penaltyRecordService,
  summaries: penaltySummaryService,
  statistics: penaltyStatisticsService,
};
